import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';

// Native iOS bundle id is the clientId on the device; the web Services ID is
// used by Supabase server-side via the dashboard config - we don't reference it here.
const IOS_CLIENT_ID = 'uk.co.teesync.app';

const generateNonce = (length = 32) => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const sha256 = async (input) => {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, '0')).join('');
};

const persistAppleName = async (givenName, familyName) => {
  const first = (givenName || '').trim();
  const last = (familyName || '').trim();
  if (!first && !last) return;
  const full = `${first} ${last}`.trim();
  await supabase.auth.updateUser({
    data: { first_name: first, last_name: last, full_name: full },
  });
};

const signInWithAppleNative = async () => {
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

  const rawNonce = generateNonce();
  const hashedNonce = await sha256(rawNonce);

  const result = await SignInWithApple.authorize({
    clientId: IOS_CLIENT_ID,
    redirectURI: 'https://teesync.co.uk/auth/callback',
    scopes: 'email name',
    nonce: hashedNonce,
  });

  const idToken = result?.response?.identityToken;
  if (!idToken) throw new Error('Apple Sign In returned no identity token');

  const { data, error } = await supabase.auth.signInWithIdToken({
    provider: 'apple',
    token: idToken,
    nonce: rawNonce,
  });
  if (error) throw error;

  // Apple sends givenName/familyName ONLY on the first authorize for a given
  // Apple ID. Persist immediately - if we miss this, the name is gone forever.
  await persistAppleName(result?.response?.givenName, result?.response?.familyName);

  return { session: data.session, user: data.user };
};

const signInWithAppleWeb = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });
  if (error) throw error;
  // Web flow redirects away; callers won't see a session synchronously.
  return { redirected: true, url: data?.url };
};

export const signInWithApple = async () => {
  if (Capacitor.getPlatform() === 'ios') {
    return signInWithAppleNative();
  }
  return signInWithAppleWeb();
};
